// StepGoodWork - GoodWork-spezifische Details (optional)
// Ausstattung, Preise, Arbeitsplaetze, Community

import React from 'react';
import { Briefcase } from 'lucide-react';
import type {
  PlaceAmenities,
  PlacePricing,
  PlaceWorkspaces,
  PlaceGoodworkEvents,
  WifiQuality,
  PowerOutletAvailability,
} from '../../../types';
import {
  WIFI_QUALITY_LABELS,
  POWER_OUTLET_LABELS,
} from '../../../types';

interface FormData {
  goodworkFactor: string;
  amenities: PlaceAmenities;
  pricing: PlacePricing;
  workspaces: PlaceWorkspaces;
  goodworkEvents: PlaceGoodworkEvents;
}

interface StepGoodWorkProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepGoodWork({ formData, updateFormData }: StepGoodWorkProps) {
  // Helper functions for nested updates
  const updateAmenities = (key: keyof PlaceAmenities, value: PlaceAmenities[keyof PlaceAmenities]) => {
    updateFormData({
      amenities: { ...formData.amenities, [key]: value },
    });
  };

  const updatePricing = (key: keyof PlacePricing, value: PlacePricing[keyof PlacePricing]) => {
    updateFormData({
      pricing: { ...formData.pricing, [key]: value },
    });
  };

  const updateWorkspaces = (key: keyof PlaceWorkspaces, value: PlaceWorkspaces[keyof PlaceWorkspaces]) => {
    updateFormData({
      workspaces: { ...formData.workspaces, [key]: value },
    });
  };

  const updateGoodworkEvents = (key: keyof PlaceGoodworkEvents, value: PlaceGoodworkEvents[keyof PlaceGoodworkEvents]) => {
    updateFormData({
      goodworkEvents: { ...formData.goodworkEvents, [key]: value },
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Briefcase className="w-5 h-5 text-indigo-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            GoodWork Details
          </h2>
        </div>
        <p className="text-gray-600">
          Diese Felder sind optional und helfen anderen, den perfekten Arbeitsplatz zu finden.
        </p>
      </div>

      {/* GoodWork Factor */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
        <label className="block text-sm font-medium text-indigo-900 mb-2">
          Was macht diesen Ort zum GoodWork Place?
        </label>
        <p className="text-sm text-indigo-700 mb-3">
          Beschreibe, warum sich dieser Ort besonders gut zum Arbeiten eignet.
        </p>
        <textarea
          value={formData.goodworkFactor || ''}
          onChange={(e) => updateFormData({ goodworkFactor: e.target.value })}
          rows={3}
          maxLength={300}
          placeholder="z.B. Hier findest du Ruhe zum konzentrierten Arbeiten mit Blick auf den Park. Die Community ist hilfsbereit und es gibt regelmaessige Gruender-Fruehstuecke."
          className="w-full px-4 py-2 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white"
        />
        <p className="text-xs text-indigo-600 mt-1 text-right">
          {(formData.goodworkFactor || '').length}/300 Zeichen
        </p>
      </div>

      {/* Ausstattung */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ausstattung</h3>

        {/* WLAN */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            WLAN-Qualitaet
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(Object.keys(WIFI_QUALITY_LABELS) as WifiQuality[]).map((quality) => (
              <button
                key={quality}
                type="button"
                onClick={() => updateAmenities('wifi', formData.amenities?.wifi === quality ? undefined : quality)}
                className={`px-3 py-2 rounded-lg text-sm transition border ${
                  formData.amenities?.wifi === quality
                    ? 'bg-indigo-500 text-white border-indigo-500'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                }`}
              >
                {WIFI_QUALITY_LABELS[quality]}
              </button>
            ))}
          </div>
        </div>

        {/* Steckdosen */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Steckdosen-Verfuegbarkeit
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(POWER_OUTLET_LABELS) as PowerOutletAvailability[]).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => updateAmenities('powerOutlets', formData.amenities?.powerOutlets === level ? undefined : level)}
                className={`px-3 py-2 rounded-lg text-sm transition border ${
                  formData.amenities?.powerOutlets === level
                    ? 'bg-indigo-500 text-white border-indigo-500'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                }`}
              >
                {POWER_OUTLET_LABELS[level]}
              </button>
            ))}
          </div>
        </div>

        {/* Weitere Ausstattung (Checkboxen) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: 'monitors' as const, label: 'Externe Monitore' },
            { key: 'printer' as const, label: 'Drucker/Scanner' },
            { key: 'kitchen' as const, label: 'Kueche/Kaffee' },
            { key: 'meetingRooms' as const, label: 'Meeting-Raeume' },
            { key: 'phoneBooths' as const, label: 'Telefonkabinen' },
            { key: 'locker' as const, label: 'Schliessfaecher' },
          ].map(({ key, label }) => (
            <label
              key={key}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                formData.amenities?.[key]
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-200'
              }`}
            >
              <input
                type="checkbox"
                checked={!!formData.amenities?.[key]}
                onChange={(e) => updateAmenities(key, e.target.checked || undefined)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                formData.amenities?.[key]
                  ? 'bg-indigo-500 border-indigo-500'
                  : 'border-gray-300'
              }`}>
                {formData.amenities?.[key] && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Preise */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preise</h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stundenpreis
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.5"
                value={formData.pricing?.hourly || ''}
                onChange={(e) => updatePricing('hourly', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="z.B. 5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">EUR</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tagespreis
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="1"
                value={formData.pricing?.daily || ''}
                onChange={(e) => updatePricing('daily', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="z.B. 25"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">EUR</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monatspreis
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="1"
                value={formData.pricing?.monthly || ''}
                onChange={(e) => updatePricing('monthly', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="z.B. 200"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">EUR</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <label
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
              formData.pricing?.dropIn
                ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-200'
            }`}
          >
            <input
              type="checkbox"
              checked={!!formData.pricing?.dropIn}
              onChange={(e) => updatePricing('dropIn', e.target.checked || undefined)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
              formData.pricing?.dropIn
                ? 'bg-indigo-500 border-indigo-500'
                : 'border-gray-300'
            }`}>
              {formData.pricing?.dropIn && (
                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="text-sm">Drop-in moeglich (ohne Vorab-Buchung)</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buchungslink
            </label>
            <input
              type="url"
              value={formData.pricing?.bookingUrl || ''}
              onChange={(e) => updatePricing('bookingUrl', e.target.value || undefined)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preis-Hinweis
            </label>
            <input
              type="text"
              value={formData.pricing?.priceNote || ''}
              onChange={(e) => updatePricing('priceNote', e.target.value || undefined)}
              placeholder="z.B. Erster Tag kostenlos, Studenten-Rabatt verfuegbar"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>
      </div>

      {/* Arbeitsplaetze */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Arbeitsplaetze</h3>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gesamtzahl Plaetze
            </label>
            <input
              type="number"
              min="0"
              value={formData.workspaces?.totalSeats || ''}
              onChange={(e) => updateWorkspaces('totalSeats', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="z.B. 30"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plaetze draussen
            </label>
            <input
              type="number"
              min="0"
              value={formData.workspaces?.outdoorSeats || ''}
              onChange={(e) => updateWorkspaces('outdoorSeats', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="z.B. 5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { key: 'quietZone' as const, label: 'Ruhebereich' },
            { key: 'teamArea' as const, label: 'Teambereich' },
            { key: 'standingDesks' as const, label: 'Steharbeitsplaetze' },
          ].map(({ key, label }) => (
            <label
              key={key}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                formData.workspaces?.[key]
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-200'
              }`}
            >
              <input
                type="checkbox"
                checked={!!formData.workspaces?.[key]}
                onChange={(e) => updateWorkspaces(key, e.target.checked || undefined)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                formData.workspaces?.[key]
                  ? 'bg-indigo-500 border-indigo-500'
                  : 'border-gray-300'
              }`}>
                {formData.workspaces?.[key] && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Community & Events */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Community & Events</h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          {[
            { key: 'networking' as const, label: 'Networking Events' },
            { key: 'workshops' as const, label: 'Workshops & Kurse' },
            { key: 'coworking' as const, label: 'Coworking Sessions' },
          ].map(({ key, label }) => (
            <label
              key={key}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                formData.goodworkEvents?.[key]
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-200'
              }`}
            >
              <input
                type="checkbox"
                checked={!!formData.goodworkEvents?.[key]}
                onChange={(e) => updateGoodworkEvents(key, e.target.checked || undefined)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                formData.goodworkEvents?.[key]
                  ? 'bg-indigo-500 border-indigo-500'
                  : 'border-gray-300'
              }`}>
                {formData.goodworkEvents?.[key] && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Community-Beschreibung
          </label>
          <textarea
            value={formData.goodworkEvents?.community || ''}
            onChange={(e) => updateGoodworkEvents('community', e.target.value || undefined)}
            rows={2}
            placeholder="z.B. Jeden Freitag Gruender-Fruehstueck, aktive Slack-Community"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
          />
        </div>
      </div>

      {/* Info-Hinweis */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p>
          <strong>Hinweis:</strong> Alle Felder sind optional. Je mehr Informationen du angibst,
          desto besser koennen andere den passenden Arbeitsplatz finden.
        </p>
      </div>
    </div>
  );
}

export default StepGoodWork;
